import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { contentService } from '../services/api';
import './css/AdminAnnouncements.css';
import {
  getPriorityColor,
  getPriorityIcon
} from './utils/AdminAnnouncements';
import {
  formatDateTime
} from './utils/Common';


const AdminAnnouncements = () => {
  const { hasPermission } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includeExpired, setIncludeExpired] = useState(false);
  const [expandedDepts, setExpandedDepts] = useState({});

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await contentService.getAllAnnouncementsByDepartment(includeExpired);
      setData(result);
      
      // Expand all departments by default
      const expanded = { global: true };
      result.departments?.forEach(dept => {
        expanded[dept.department] = true;
      });
      setExpandedDepts(expanded);
    } catch (err) {
      console.error('Error fetching announcements:', err);
      setError('Failed to load announcements. Make sure you have admin permissions.');
    } finally {
      setLoading(false);
    }
  }, [includeExpired]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleDept = (deptName) => {
    setExpandedDepts(prev => ({
      ...prev,
      [deptName]: !prev[deptName]
    }));
  };

  const renderAnnouncementCard = (announcement) => (
    <div 
      key={announcement.id} 
      className={`announcement-card priority-${announcement.priority}`}
      style={{ borderLeftColor: getPriorityColor(announcement.priority) }}
    >
      <div className="announcement-header">
        <span className="priority-icon">{getPriorityIcon(announcement.priority)}</span>
        <span className="announcement-title">{announcement.title}</span>
        {announcement.is_pinned && <span className="pinned-badge">📌 Pinned</span>}
        <span className="priority-badge" style={{ backgroundColor: getPriorityColor(announcement.priority) }}>
          {announcement.priority.toUpperCase()}
        </span>
      </div>
      <p className="announcement-content">{announcement.content}</p>
      <div className="announcement-footer">
        <span className="announcement-author">By {announcement.author_name}</span>
        <span className="announcement-date">{formatDateTime(announcement.created_at)}</span>
        {announcement.expires_at && (
          <span className="announcement-expires">
            Expires: {formatDateTime(announcement.expires_at)}
          </span>
        )}
      </div>
    </div>
  );

  if (!hasPermission('content:manage_announcements')) {
    return (
      <div className="page-container">
        <div className="error-message">
          You do not have permission to view all announcements.
        </div>
      </div>
    );
  }

  return (
    <div className="page-container admin-announcements">
      <div className="page-header">
        <h2>📢 All Announcements Overview</h2>
        <p>View announcements across all departments</p>
      </div>

      <div className="filter-controls">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={includeExpired}
            onChange={(e) => setIncludeExpired(e.target.checked)}
          />
          Include expired announcements
        </label>
        <button className="btn btn-secondary" onClick={fetchData}>
          🔄 Refresh
        </button>
      </div>

      {loading ? (
        <div className="loading-spinner">Loading announcements...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <div className="announcements-overview">
          {/* Global Announcements Section */}
          <div className="department-section">
            <div 
              className="department-header clickable"
              onClick={() => toggleDept('global')}
            >
              <span className="expand-icon">{expandedDepts['global'] ? '▼' : '▶'}</span>
              <h3>🌐 Company-Wide (Global)</h3>
              <span className="announcement-count">
                {data?.global_announcements?.length || 0} announcements
              </span>
            </div>
            {expandedDepts['global'] && (
              <div className="department-announcements">
                {data?.global_announcements?.length > 0 ? (
                  data.global_announcements.map(renderAnnouncementCard)
                ) : (
                  <p className="no-content">No global announcements</p>
                )}
              </div>
            )}
          </div>

          {/* Department Sections */}
          {data?.departments?.map(dept => (
            <div key={dept.department} className="department-section">
              <div 
                className="department-header clickable"
                onClick={() => toggleDept(dept.department)}
              >
                <span className="expand-icon">{expandedDepts[dept.department] ? '▼' : '▶'}</span>
                <h3>🏢 {dept.department.charAt(0).toUpperCase() + dept.department.slice(1)}</h3>
                <span className="announcement-count">
                  {dept.announcements?.length || 0} announcements
                </span>
              </div>
              {expandedDepts[dept.department] && (
                <div className="department-announcements">
                  {dept.announcements?.length > 0 ? (
                    dept.announcements.map(renderAnnouncementCard)
                  ) : (
                    <p className="no-content">No announcements in this department</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminAnnouncements;
